import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { markOnboardingCompletedInCache } from '@/components/OnboardingCheck';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChatBubble } from './ChatBubble';
import { TypewriterMessage } from './TypewriterMessage';
import { toast } from 'sonner';
import { Shield, Heart, Activity, Brain, Sparkles, Calendar, Target, History, ChevronRight, Send, X, ArrowLeft, ArrowRight } from 'lucide-react';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import WelcomeStep from './WelcomeStep';
import { useTourController } from '@/hooks/useTourController';
import { diagLog } from '@/utils/diagnosticSession';
import { markOnboardingDone } from '@/hooks/useOnboardingStateMachine';

type ConversationStep = 
  | 'feature_welcome'  // NEW: Full-page welcome with feature list
  | 'welcome'
  | 'name'
  | 'name_response'
  | 'security'
  | 'motivation_intro'
  | 'motivation'
  | 'journeys_intro'
  | 'journeys'
  | 'depth_intro'
  | 'depth'
  | 'style_intro'
  | 'style'
  | 'complete';

interface OnboardingData {
  name: string;
  motivations: string[];
  journeys: string[];
  engagementDepth: 'light' | 'standard' | 'deep';
  guidanceStyle: 'scientific' | 'spiritual' | 'blended';
}

const MOTIVATIONS = [
  { id: 'calm_mind', label: 'Calm my mind', icon: Heart },
  { id: 'support_body', label: 'Support my body', icon: Activity },
  { id: 'nourish_body', label: 'Nourish & heal', icon: Brain },
  { id: 'therapeutic_support', label: 'Gain reflective support', icon: Shield },
  { id: 'heal_past', label: 'Heal from my past', icon: History },
  { id: 'balance_cycle', label: 'Balance my cycle', icon: Calendar },
  { id: 'spiritual_connection', label: 'Deepen spiritually', icon: Sparkles },
  { id: 'future_self', label: 'Align with my future self', icon: Target },
];

const JOURNEYS = [
  { id: 'mental_wellness', label: 'Mental Wellness', icon: Brain },
  { id: 'physical_health', label: 'Physical Health', icon: Activity },
  { id: 'womens_wellness', label: "Women's Wellness", icon: Calendar },
  { id: 'spiritual_development', label: 'Spiritual Growth', icon: Sparkles },
  { id: 'future_self', label: 'Future Self', icon: Target },
  { id: 'heal_timeline', label: 'Heal My Timeline', icon: History },
];

const DEPTHS = [
  { id: 'light', label: 'Keep it simple', description: 'Quick check-ins' },
  { id: 'standard', label: 'Balanced pace', description: 'Regular engagement' },
  { id: 'deep', label: 'Deep dive', description: 'Full experience' },
];

const STYLES = [
  { id: 'scientific', label: 'Scientific', description: 'Evidence-based' },
  { id: 'spiritual', label: 'Spiritual', description: 'Intuitive guidance' },
  { id: 'blended', label: 'Blended', description: 'Best of both' },
];

// Floating particles component
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-white/20 rounded-full"
        initial={{ 
          x: Math.random() * 100 + '%', 
          y: '110%',
          opacity: 0 
        }}
        animate={{ 
          y: '-10%',
          opacity: [0, 0.6, 0],
        }}
        transition={{
          duration: 8 + Math.random() * 6,
          repeat: Infinity,
          delay: Math.random() * 8,
          ease: 'linear'
        }}
        style={{ left: `${Math.random() * 100}%` }}
      />
    ))}
  </div>
);

// Ambient glow orbs
const AmbientOrbs = () => (
  <>
    <motion.div 
      className="absolute top-1/4 -left-32 w-96 h-96 bg-[hsl(177,41%,50%)]/20 rounded-full blur-[120px]"
      animate={{ 
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
        x: [0, 30, 0]
      }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div 
      className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[hsl(182,37%,35%)]/30 rounded-full blur-[120px]"
      animate={{ 
        scale: [1.2, 1, 1.2],
        opacity: [0.4, 0.6, 0.4],
        x: [0, -30, 0]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[hsl(177,41%,60%)]/10 rounded-full blur-[150px]"
      animate={{ 
        scale: [1, 1.1, 1],
        opacity: [0.2, 0.3, 0.2]
      }}
      transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
    />
  </>
);

interface ConversationalOnboardingProps {
  onComplete?: () => void;
}

export const ConversationalOnboarding: React.FC<ConversationalOnboardingProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const tourController = useTourController();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // CRITICAL: Start at 'welcome' step, NOT 'feature_welcome'
  // The 4-box welcome (feature_welcome → WelcomeStep) was already shown in pre-auth
  // Starting here prevents the duplicate 4-box screen after authentication
  const [step, setStep] = useState<ConversationStep>('welcome');

  // DIAGNOSTIC: Log on mount
  useEffect(() => {
    diagLog('ConversationalOnboarding', 'MOUNT', { initialStep: 'welcome' });
  }, []);
  const [showInput, setShowInput] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [nameInput, setNameInput] = useState('');
  // Track which steps have completed their typewriter effect
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  
  const [data, setData] = useState<OnboardingData>({
    name: '',
    motivations: [],
    journeys: [],
    engagementDepth: 'standard',
    guidanceStyle: 'blended'
  });

  // Check if current step's typewriter has completed
  const isStepComplete = (stepName: string) => completedSteps.has(stepName);
  
  // Mark a step as complete
  const markStepComplete = (stepName: string) => {
    setCompletedSteps(prev => new Set([...prev, stepName]));
  };

  // Track last scroll position to prevent jumping
  const lastScrollPositionRef = useRef<number>(0);
  
  // Smooth scroll to bottom - premium feel with eased animation
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      // Animate scroll smoothly
      const start = container.scrollTop;
      const target = container.scrollHeight - container.clientHeight;
      const duration = 300;
      const startTime = performance.now();
      
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      
      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        
        container.scrollTop = start + (target - start) * easedProgress;
        lastScrollPositionRef.current = container.scrollTop;
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    }
  }, []);
  
  // Instant scroll for typewriter updates - uses immediate scroll but remembers position
  const scrollOnTypewriter = useCallback(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      // Only scroll if we're near the bottom already (user hasn't manually scrolled up)
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
        lastScrollPositionRef.current = container.scrollTop;
      }
    }
  }, []);

  // After selection steps complete, ensure latest content is visible
  const scrollAfterSelection = useCallback(() => {
    if (scrollRef.current) {
      // Small delay to let DOM update after Continue click
      setTimeout(() => {
        if (scrollRef.current) {
          const container = scrollRef.current;
          // Smooth scroll to bottom to show new typewriter message
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
          lastScrollPositionRef.current = container.scrollTop;
        }
      }, 100);
    }
  }, []);

  // When step changes, scroll appropriately
  useEffect(() => {
    // For all steps including option steps, scroll to bottom to show latest content
    // The key insight: we ALWAYS want to see the newest message/options
    scrollToBottom();
  }, [step, showOptions, completedSteps, scrollToBottom]);

  // This is called by TypewriterMessage on each character update - smooth and consistent
  const handleTypewriterUpdate = useCallback(() => {
    scrollOnTypewriter();
  }, [scrollOnTypewriter]);

  // Focus input when shown
  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameInput.trim()) {
      setData(prev => ({ ...prev, name: nameInput.trim() }));
      setShowInput(false);
      setStep('name_response');
    }
  };

  const handleMotivationSelect = (id: string) => {
    setData(prev => ({
      ...prev,
      motivations: prev.motivations.includes(id)
        ? prev.motivations.filter(m => m !== id)
        : [...prev.motivations, id]
    }));
  };

  const handleJourneySelect = (id: string) => {
    setData(prev => ({
      ...prev,
      journeys: prev.journeys.includes(id)
        ? prev.journeys.filter(j => j !== id)
        : [...prev.journeys, id]
    }));
  };

  const handleContinue = () => {
    setShowOptions(false);
    
    // Scroll to bottom to show the next typewriter message
    scrollAfterSelection();
    
    switch (step) {
      case 'motivation':
        setStep('journeys_intro');
        break;
      case 'journeys':
        setStep('depth_intro');
        break;
      case 'depth':
        setStep('style_intro');
        break;
      case 'style':
        setStep('complete');
        break;
    }
  };

  const handleDepthSelect = (depth: 'light' | 'standard' | 'deep') => {
    setData(prev => ({ ...prev, engagementDepth: depth }));
  };

  const handleStyleSelect = (style: 'scientific' | 'spiritual' | 'blended') => {
    setData(prev => ({ ...prev, guidanceStyle: style }));
  };

  const completeOnboarding = async () => {
    diagLog('ConversationalOnboarding', 'COMPLETE_START', {});
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // NOTE: We do NOT call markOnboardingComplete() here anymore.
      // That was causing the state machine to skip the 'welcome' state.
      // The transition protection is now handled by the state machine itself.
      diagLog('ConversationalOnboarding', 'SAVING_PROFILE', { userId: user.id });

      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: data.name,
          primary_motivations: data.motivations.length > 0 ? data.motivations : ['calm_mind'],
          active_journeys: data.journeys.length > 0 ? data.journeys : ['mental_wellness'],
          engagement_depth: data.engagementDepth,
          guidance_style: data.guidanceStyle,
          onboarding_completed: true,
          data_sources: { daily_checkin: true, whoop: false, cycle_tracking: false }
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Mark onboarding as complete in BOTH the legacy cache AND the state machine
      markOnboardingCompletedInCache(user.id);
      markOnboardingDone(); // This triggers state transition to 'tour' stage
      
      // CRITICAL: Set durable + one-time flags for tour auto-start
      tourController.markConversationalComplete();
      diagLog('ConversationalOnboarding', 'STATE_MACHINE_UPDATED', { 
        newOnboardingCompleted: true,
        conversationalCompletedFlagSet: true
      });

      toast.success(`Welcome to Therappi®, ${data.name}!`);
      onComplete?.();
      
      // Navigate to dashboard - state machine will now be in 'tour' stage
      // TourContainer will auto-start the tour
      console.log('[ConversationalOnboarding] Complete - state machine moves to tour stage');
      navigate('/');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Clear the transition flag on error so user isn't stuck
      tourController.endTour();
      toast.error('Something went wrong. Please try again.');
    }
  };

  const renderMessages = () => {
    const messages: React.ReactNode[] = [];

    // Welcome message
    messages.push(
      <ChatBubble key="welcome" animate={step === 'welcome' && !isStepComplete('welcome')}>
        {step === 'welcome' && !isStepComplete('welcome') ? (
          <TypewriterMessage 
            text="Hello, and welcome to Therappi®. I'm so glad you're here."
            onComplete={() => {
              markStepComplete('welcome');
              setTimeout(() => setStep('name'), 300);
            }}
            onUpdate={handleTypewriterUpdate}
          />
        ) : (
          "Hello, and welcome to Therappi®. I'm so glad you're here."
        )}
      </ChatBubble>
    );

    // Name request
    if (['name', 'name_response', 'security', 'motivation_intro', 'motivation', 'journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="name_ask" animate={step === 'name' && !isStepComplete('name_ask')}>
          {step === 'name' && !isStepComplete('name_ask') ? (
            <TypewriterMessage 
              text="Before we begin, I'd love to know what to call you?"
              onComplete={() => {
                markStepComplete('name_ask');
                setShowInput(true);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            "Before we begin, I'd love to know what to call you?"
          )}
        </ChatBubble>
      );
    }

    // User's name response
    if (data.name && step !== 'name') {
      messages.push(
        <ChatBubble key="name_user" isUser>
          {data.name}
        </ChatBubble>
      );
    }

    // Name response message
    if (['name_response', 'security', 'motivation_intro', 'motivation', 'journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="name_response" animate={step === 'name_response' && !isStepComplete('name_response')}>
          {step === 'name_response' && !isStepComplete('name_response') ? (
            <TypewriterMessage 
              text={`It's lovely to meet you, ${data.name}. ✨`}
              onComplete={() => {
                markStepComplete('name_response');
                setTimeout(() => setStep('security'), 250);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            `It's lovely to meet you, ${data.name}. ✨`
          )}
        </ChatBubble>
      );
    }

    // Security message
    if (['security', 'motivation_intro', 'motivation', 'journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="security" animate={step === 'security' && !isStepComplete('security')}>
          <div className="flex items-start gap-3">
            <div className="p-1.5 rounded-full bg-[hsl(177,41%,76%)]/20">
              <Shield className="w-4 h-4 text-[hsl(177,41%,76%)]" />
            </div>
            <div className="flex-1">
              {step === 'security' && !isStepComplete('security') ? (
                <TypewriterMessage 
                  text="This is your private space. Everything you share here stays encrypted and secure-just between us."
                  onComplete={() => {
                    markStepComplete('security');
                    setTimeout(() => setStep('motivation_intro'), 800);
                  }}
                  onUpdate={handleTypewriterUpdate}
                />
              ) : (
                "This is your private space. Everything you share here stays encrypted and secure-just between us."
              )}
            </div>
          </div>
        </ChatBubble>
      );
    }

    // Motivation intro
    if (['motivation_intro', 'motivation', 'journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="motivation_intro" animate={step === 'motivation_intro' && !isStepComplete('motivation_intro')}>
          {step === 'motivation_intro' && !isStepComplete('motivation_intro') ? (
            <TypewriterMessage 
              text="To help me support you in the best way, could you tell me what brings you here? You can select all that apply."
              onComplete={() => {
                markStepComplete('motivation_intro');
                // Extended delay (2200ms) before showing cards so user can fully read the message
                setTimeout(() => {
                  setStep('motivation');
                  setShowOptions(true);
                }, 2200);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            "To help me support you in the best way, could you tell me what brings you here? You can select all that apply."
          )}
        </ChatBubble>
      );
    }

    // Motivation selection
    if (['motivation', 'journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      if (step === 'motivation' && showOptions) {
        messages.push(
          <motion.div 
            key="motivation_options"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-3 pl-4"
          >
            <div className="grid grid-cols-2 gap-2.5">
              {MOTIVATIONS.map((m, idx) => {
                const Icon = m.icon;
                const selected = data.motivations.includes(m.id);
                
                return (
                  <motion.button
                    key={m.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleMotivationSelect(m.id)}
                    className={`
                      group relative flex items-center gap-2 p-3.5 rounded-xl text-left text-sm
                      transition-all duration-300 backdrop-blur-sm min-h-[60px]
                      ${selected 
                        ? 'bg-[hsl(177,41%,50%)]/25 border-2 border-[hsl(177,41%,76%)]/60 text-white shadow-lg shadow-[hsl(177,41%,50%)]/25' 
                        : 'bg-white/8 border border-white/15 text-white/85 hover:bg-white/12 hover:border-white/30'
                      }
                    `}
                  >
                    <div className={`
                      p-1.5 rounded-lg transition-all duration-300 flex-shrink-0
                      ${selected ? 'bg-[hsl(177,41%,76%)]/40' : 'bg-white/10 group-hover:bg-white/15'}
                    `}>
                      <Icon className={`w-4 h-4 ${selected ? 'text-white' : ''}`} />
                    </div>
                    <span className={`font-light text-[13px] leading-tight ${selected ? 'font-normal' : ''}`}>{m.label}</span>
                    {selected && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-2.5 h-2.5 bg-[hsl(177,41%,76%)] rounded-full shadow-md shadow-[hsl(177,41%,76%)]/50"
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button 
                onClick={handleContinue} 
                disabled={data.motivations.length === 0}
                variant="gradient"
                className="w-full mt-2"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </motion.div>
        );
      } else if (step !== 'motivation' && data.motivations.length > 0) {
        messages.push(
          <ChatBubble key="motivation_user" isUser>
            {data.motivations.map(m => MOTIVATIONS.find(mo => mo.id === m)?.label).join(', ')}
          </ChatBubble>
        );
      }
    }

    // Journeys intro
    if (['journeys_intro', 'journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="journeys_intro" animate={step === 'journeys_intro' && !isStepComplete('journeys_intro')}>
          {step === 'journeys_intro' && !isStepComplete('journeys_intro') ? (
            <TypewriterMessage 
              text="Beautiful. Now, which areas would you like to explore together? Again, select all that apply."
              onComplete={() => {
                markStepComplete('journeys_intro');
                setTimeout(() => {
                  setStep('journeys');
                  setShowOptions(true);
                }, 200);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            "Beautiful. Now, which areas would you like to explore together? Again, select all that apply."
          )}
        </ChatBubble>
      );
    }

    // Journeys selection
    if (['journeys', 'depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      if (step === 'journeys' && showOptions) {
        messages.push(
          <motion.div 
            key="journeys_options"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-3 pl-4"
          >
            <div className="grid grid-cols-2 gap-2.5">
              {JOURNEYS.map((j, idx) => {
                const Icon = j.icon;
                const selected = data.journeys.includes(j.id);
                
                return (
                  <motion.button
                    key={j.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleJourneySelect(j.id)}
                    className={`
                      group relative flex items-center gap-2 p-3.5 rounded-xl text-left text-sm
                      transition-all duration-300 backdrop-blur-sm min-h-[60px]
                      ${selected 
                        ? 'bg-[hsl(177,41%,50%)]/25 border-2 border-[hsl(177,41%,76%)]/60 text-white shadow-lg shadow-[hsl(177,41%,50%)]/25' 
                        : 'bg-white/8 border border-white/15 text-white/85 hover:bg-white/12 hover:border-white/30'
                      }
                    `}
                  >
                    <div className={`
                      p-1.5 rounded-lg transition-all duration-300 flex-shrink-0
                      ${selected ? 'bg-[hsl(177,41%,76%)]/40' : 'bg-white/10 group-hover:bg-white/15'}
                    `}>
                      <Icon className={`w-4 h-4 ${selected ? 'text-white' : ''}`} />
                    </div>
                    <span className={`font-light text-[13px] leading-tight ${selected ? 'font-normal' : ''}`}>{j.label}</span>
                    {selected && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-2.5 h-2.5 bg-[hsl(177,41%,76%)] rounded-full shadow-md shadow-[hsl(177,41%,76%)]/50"
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button 
                onClick={handleContinue} 
                disabled={data.journeys.length === 0}
                variant="gradient"
                className="w-full mt-2"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </motion.div>
        );
      } else if (step !== 'journeys' && data.journeys.length > 0) {
        messages.push(
          <ChatBubble key="journeys_user" isUser>
            {data.journeys.map(j => JOURNEYS.find(jo => jo.id === j)?.label).join(', ')}
          </ChatBubble>
        );
      }
    }

    // Depth intro
    if (['depth_intro', 'depth', 'style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="depth_intro" animate={step === 'depth_intro' && !isStepComplete('depth_intro')}>
          {step === 'depth_intro' && !isStepComplete('depth_intro') ? (
            <TypewriterMessage 
              text="How would you like to start? We can always adjust as we go."
              onComplete={() => {
                markStepComplete('depth_intro');
                setTimeout(() => {
                  setStep('depth');
                  setShowOptions(true);
                }, 200);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            "How would you like to start? We can always adjust as we go."
          )}
        </ChatBubble>
      );
    }

    // Depth selection
    if (['depth', 'style_intro', 'style', 'complete'].includes(step)) {
      if (step === 'depth' && showOptions) {
        messages.push(
          <motion.div 
            key="depth_options"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-2.5 pl-4"
          >
            {DEPTHS.map((d, idx) => {
              const selected = data.engagementDepth === d.id;
              return (
                <motion.button
                  key={d.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => handleDepthSelect(d.id as any)}
                  className={`
                    w-full flex items-center justify-between p-4 rounded-xl text-left
                    transition-all duration-300 backdrop-blur-sm
                    ${selected 
                      ? 'bg-[hsl(177,41%,50%)]/25 border-2 border-[hsl(177,41%,76%)]/60 text-white shadow-lg shadow-[hsl(177,41%,50%)]/25' 
                      : 'bg-white/8 border border-white/15 text-white/80 hover:bg-white/12 hover:border-white/30'
                    }
                  `}
                >
                  <span className={`font-light ${selected ? 'font-normal' : ''}`}>{d.label}</span>
                  <span className={`text-xs ${selected ? 'text-white/70' : 'text-white/50'}`}>{d.description}</span>
                </motion.button>
              );
            })}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Button 
                onClick={handleContinue}
                variant="gradient"
                className="w-full mt-2"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </motion.div>
        );
      } else if (step !== 'depth') {
        messages.push(
          <ChatBubble key="depth_user" isUser>
            {DEPTHS.find(d => d.id === data.engagementDepth)?.label}
          </ChatBubble>
        );
      }
    }

    // Style intro
    if (['style_intro', 'style', 'complete'].includes(step)) {
      messages.push(
        <ChatBubble key="style_intro" animate={step === 'style_intro' && !isStepComplete('style_intro')}>
          {step === 'style_intro' && !isStepComplete('style_intro') ? (
            <TypewriterMessage 
              text="One last thing-how would you like me to speak with you?"
              onComplete={() => {
                markStepComplete('style_intro');
                setTimeout(() => {
                  setStep('style');
                  setShowOptions(true);
                }, 200);
              }}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            "One last thing-how would you like me to speak with you?"
          )}
        </ChatBubble>
      );
    }

    // Style selection
    if (['style', 'complete'].includes(step)) {
      if (step === 'style' && showOptions) {
        messages.push(
          <motion.div 
            key="style_options"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-2.5 pl-4"
          >
            {STYLES.map((s, idx) => {
              const selected = data.guidanceStyle === s.id;
              return (
                <motion.button
                  key={s.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => handleStyleSelect(s.id as any)}
                  className={`
                    w-full flex items-center justify-between p-4 rounded-xl text-left
                    transition-all duration-300 backdrop-blur-sm
                    ${selected 
                      ? 'bg-[hsl(177,41%,50%)]/25 border-2 border-[hsl(177,41%,76%)]/60 text-white shadow-lg shadow-[hsl(177,41%,50%)]/25' 
                      : 'bg-white/8 border border-white/15 text-white/80 hover:bg-white/12 hover:border-white/30'
                    }
                  `}
                >
                  <span className={`font-light ${selected ? 'font-normal' : ''}`}>{s.label}</span>
                  <span className={`text-xs ${selected ? 'text-white/70' : 'text-white/50'}`}>{s.description}</span>
                </motion.button>
              );
            })}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Button 
                onClick={handleContinue}
                variant="gradient"
                className="w-full mt-2"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </motion.div>
        );
      } else if (step === 'complete') {
        messages.push(
          <ChatBubble key="style_user" isUser>
            {STYLES.find(s => s.id === data.guidanceStyle)?.label}
          </ChatBubble>
        );
      }
    }

    // Completion
    if (step === 'complete') {
      messages.push(
        <ChatBubble key="complete" animate={!isStepComplete('complete')}>
          {!isStepComplete('complete') ? (
            <TypewriterMessage 
              text={`Perfect, ${data.name}. Your personal wellness space is ready. Let's begin your journey together.`}
              onComplete={() => markStepComplete('complete')}
              onUpdate={handleTypewriterUpdate}
            />
          ) : (
            `Perfect, ${data.name}. Your personal wellness space is ready. Let's begin your journey together.`
          )}
        </ChatBubble>
      );
      
      if (isStepComplete('complete')) {
        messages.push(
          <motion.div
            key="complete_button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="pl-4 space-y-3"
          >
            <Button 
              onClick={completeOnboarding}
              variant="gradientLight"
              className="w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Begin My Journey
            </Button>
            <p className="text-xs text-white/40 text-center">
              All features remain accessible. You can adjust your preferences anytime in Settings.
            </p>
          </motion.div>
        );
      }
    }

    return messages;
  };

  // If on feature_welcome step, show the full-page WelcomeStep
  if (step === 'feature_welcome') {
    return (
      <WelcomeStep onContinue={() => setStep('welcome')} />
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[hsl(182,29%,20%)] via-[hsl(182,29%,26%)] to-[hsl(182,37%,22%)] overflow-hidden">
      {/* Ambient background effects */}
      <AmbientOrbs />
      <FloatingParticles />
      
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Main content container */}
      <div className="relative z-10 flex flex-col h-full max-w-2xl mx-auto px-4">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="py-8 text-center"
        >
          <img 
            src={therappiLogo} 
            alt="Therappi" 
            className="h-8 mx-auto opacity-90 drop-shadow-lg"
          />
        </motion.header>

        {/* Chat container */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 flex flex-col min-h-0"
        >
          {/* Glass card */}
          <div className="flex-1 flex flex-col bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl shadow-black/20 overflow-hidden">
            {/* Chat header */}
            <div className="px-6 py-4 border-b border-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(177,41%,76%)] to-[hsl(182,37%,46%)] flex items-center justify-center shadow-lg shadow-[hsl(177,41%,76%)]/20">
                      <Sparkles className="w-5 h-5 text-[hsl(182,29%,20%)]" />
                    </div>
                    <motion.div 
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[hsl(182,29%,26%)]"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </div>
                  <div>
                    <h2 className="text-white font-medium tracking-tight">Therappi<sup className="text-[0.6em] align-super">®</sup> Guide</h2>
                    <p className="text-xs text-white/40">Here to help you get started</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors text-white/60 hover:text-white"
                  title="Skip onboarding"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages area - increased padding and min-height for better visibility */}
            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-6 pb-8 space-y-5 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent min-h-[50vh]"
            >
              <AnimatePresence mode="sync">
                {renderMessages()}
              </AnimatePresence>
              {/* Extra padding at bottom to ensure last message is visible */}
              <div className="h-6" />
            </div>

            {/* Input area - show after typewriter completes for name step */}
            {step === 'name' && showInput && !data.name && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border-t border-white/5"
              >
                <form onSubmit={handleNameSubmit} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Enter your name..."
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[hsl(177,41%,76%)]/50 focus:ring-[hsl(177,41%,76%)]/20"
                  />
                  <Button 
                    type="submit" 
                    disabled={!nameInput.trim()}
                    variant="gradientLight"
                    size="icon"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="py-6 text-center"
        >
          <p className="text-white/30 text-sm font-light tracking-widest">
            Where Science Meets Soul
          </p>
        </motion.footer>
      </div>
    </div>
  );
};

export default ConversationalOnboarding;
